import {
	Box,
	Drawer,
	IconButton,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MapView from "../../../components/MapComponent/MapView";
import ListIcon from "@mui/icons-material/List";
import CloseIcon from "@mui/icons-material/Close";
import GroupsIcon from "@mui/icons-material/Groups";
import HistoryIcon from "@mui/icons-material/History";
import LocationList from "./LocationList";
import { LocationProvider } from "./LocationContext";
import VehicleCamerasLivePanel from "./VehicleCamerasLivePanel";
import GeofencePanel from "./GeofencePanel";
import LocationVehicleRosterPanel from "./LocationVehicleRosterPanel";
import GeofenceHistoryPanel from "./GeofenceHistoryPanel";
import { AppContext } from "../../../context/AppContextProvider";
import type { GeofenceCenter, Geofence } from "./geofenceUtils";

const GEOFENCE_STORAGE_KEY = "alertai:location-geofences:v1";
const GEOFENCE_API_BASE =
	typeof window !== "undefined" && window.location.hostname === "localhost"
		? `http://${window.location.hostname}:5298/v1/Geofences`
		: "/v1/Geofences";

function loadGeofencesFromStorage(): Geofence[] {
	if (typeof window === "undefined") {
		return [];
	}

	try {
		const rawValue = window.localStorage.getItem(GEOFENCE_STORAGE_KEY);
		if (!rawValue) {
			return [];
		}

		const parsed = JSON.parse(rawValue) as unknown[];
		if (!Array.isArray(parsed)) return [];

		return parsed.map((p, idx) => {
			const item = p as Record<string, unknown>;
			const center = item.center as Record<string, unknown> | undefined;
			return {
				id: typeof item.id === "string" ? item.id : `gf_${idx}`,
				name: typeof item.name === "string" ? item.name : undefined,
				enabled: Boolean(item.enabled),
				center:
					center &&
					typeof center.latitude === "number" &&
					typeof center.longitude === "number"
						? {
								latitude: center.latitude as number,
								longitude: center.longitude as number,
							}
						: null,
				radiusMeters: Math.max(50, Number(item.radiusMeters) || 300),
			} as Geofence;
		});
	} catch {
		return [];
	}
}

function normalizeGeofenceSearchQuery(query: string) {
	const trimmed = query.trim();
	const digitsOnly = trimmed.replace(/\D/g, "");

	if (digitsOnly.length === 8) {
		return `${digitsOnly.slice(0, 5)}-${digitsOnly.slice(5)}`;
	}

	return trimmed;
}

async function resolveCenterFromAddressOrCep(query: string) {
	const normalizedQuery = normalizeGeofenceSearchQuery(query);
	if (!normalizedQuery) {
		return null;
	}

	const url = new URL("https://nominatim.openstreetmap.org/search");
	url.searchParams.set("format", "jsonv2");
	url.searchParams.set("limit", "1");
	url.searchParams.set("countrycodes", "br");
	url.searchParams.set("addressdetails", "1");
	url.searchParams.set("q", normalizedQuery);

	const response = await fetch(url.toString(), {
		headers: {
			Accept: "application/json",
			"Accept-Language": "pt-BR,pt;q=0.9",
		},
	});

	if (!response.ok) {
		throw new Error("Geocoding request failed");
	}

	const results = (await response.json()) as Array<{
		lat: string;
		lon: string;
		display_name?: string;
	}>;

	if (!Array.isArray(results) || results.length === 0) {
		return null;
	}

	const firstResult = results[0];
	const latitude = Number(firstResult.lat);
	const longitude = Number(firstResult.lon);

	if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
		return null;
	}

	return {
		latitude,
		longitude,
		description: firstResult.display_name,
	};
}

const LocationIndex = () => {
	const { t } = useTranslation();
	const { showSnackbar } = useContext(AppContext);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const isMobile = useMediaQuery("(max-width:900px)");
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const [vehicleRosterOpen, setVehicleRosterOpen] = useState(false);
	const [geofenceHistoryOpen, setGeofenceHistoryOpen] = useState(false);
	const [geofencePanelOpen, setGeofencePanelOpen] = useState(false);
	const [geofences, setGeofences] = useState<Geofence[]>([]);
	const [selectedGeofenceId, setSelectedGeofenceId] = useState<string | null>(
		null,
	);
	const [focusedGeofenceId, setFocusedGeofenceId] = useState<string | null>(
		null,
	);
	const [isPickingFenceCenter, setIsPickingFenceCenter] = useState(false);
	const [pickingGeofenceId, setPickingGeofenceId] = useState<string | null>(
		null,
	);

	useEffect(() => {
		let mounted = true;

		async function load() {
			try {
				const res = await fetch(GEOFENCE_API_BASE, {
					headers: { Accept: "application/json" },
				});
				if (!res.ok) throw new Error("api-error");
				const data = await res.json();
				if (!mounted) return;
				const converted: Geofence[] = (
					data as Array<Record<string, unknown>>
				).map((d, idx) => {
					const name = typeof d.name === "string" ? d.name : `Cerca ${idx + 1}`;
					const id = String(d.id ?? `gf_${idx}`);
					const enabled = Boolean(d.enabled);
					const hasCenter = Boolean(d.centerLatitude && d.centerLongitude);

					return {
						id,
						name,
						enabled,
						center: hasCenter
							? {
									latitude: Number(d.centerLatitude),
									longitude: Number(d.centerLongitude),
								}
							: null,
						radiusMeters: Number(d.radiusMeters ?? 300),
					};
				});
				setGeofences(converted);
				return;
			} catch {
				const local = loadGeofencesFromStorage();
				setGeofences(local);
			}
		}

		load();

		return () => {
			mounted = false;
		};
	}, []);

	async function apiCreateGeofence(dto: Partial<Geofence>) {
		try {
			const payload = {
				name: dto.name,
				enabled: dto.enabled,
				centerLatitude: dto.center?.latitude,
				centerLongitude: dto.center?.longitude,
				radiusMeters: dto.radiusMeters,
			};
			const res = await fetch(GEOFENCE_API_BASE, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});
			if (!res.ok) throw new Error("create-failed");
			return await res.json();
		} catch {
			showSnackbar(t("map.createFenceFailed"), "warning");
			return null;
		}
	}

	async function apiUpdateGeofence(id: string, dto: Partial<Geofence>) {
		try {
			const payload = {
				name: dto.name,
				enabled: dto.enabled,
				centerLatitude: dto.center?.latitude,
				centerLongitude: dto.center?.longitude,
				radiusMeters: dto.radiusMeters,
			};
			const res = await fetch(`${GEOFENCE_API_BASE}/${id}`, {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			});
			if (!res.ok) throw new Error("update-failed");
			return await res.json();
		} catch {
			showSnackbar(t("map.updateFenceFailed"), "warning");
			return null;
		}
	}

	async function apiDeleteGeofence(id: string) {
		try {
			const res = await fetch(`${GEOFENCE_API_BASE}/${id}`, {
				method: "DELETE",
			});
			if (!res.ok) throw new Error("delete-failed");
			return true;
		} catch {
			showSnackbar(t("map.deleteFenceFailed"), "warning");
			return false;
		}
	}

	const geofenceConfig = useMemo(() => geofences, [geofences]);

	const sharedLocationListProps = {
		selectedIds,
		setSelectedIds,
	};

	const handleSelect = (id: string) => {
		setSelectedId(id);
		if (isMobile) {
			setSidebarOpen(false);
		}
	};

	const handleStopFollow = () => {
		setSelectedId(null);
	};

	const handleFenceCenterSelected = (id: string, center: GeofenceCenter) => {
		setGeofences((prev) =>
			prev.map((g) => (g.id === id ? { ...g, center, enabled: true } : g)),
		);
		apiUpdateGeofence(id, { center, enabled: true }).catch(() => {});
		setIsPickingFenceCenter(false);
		setPickingGeofenceId(null);
		showSnackbar(t("map.fenceCenterDefined"), "success");
	};

	const handleSearchFenceCenter = async (id: string, query: string) => {
		try {
			const resolvedCenter = await resolveCenterFromAddressOrCep(query);
			if (!resolvedCenter) {
				showSnackbar(t("map.fenceCenterNotFound"), "warning");
				return false;
			}

			setGeofences((prev) =>
				prev.map((g) =>
					g.id === id
						? {
								...g,
								center: {
									latitude: resolvedCenter.latitude,
									longitude: resolvedCenter.longitude,
								},
								enabled: true,
							}
						: g,
				),
			);

			apiUpdateGeofence(id, {
				center: {
					latitude: resolvedCenter.latitude,
					longitude: resolvedCenter.longitude,
				},
				enabled: true,
			}).catch(() => {});

			showSnackbar(
				resolvedCenter.description
					? t("map.fenceCenterSetTo", {
							description: resolvedCenter.description,
						})
					: t("map.fenceCenterAddressDefined"),
				"success",
			);
			return true;
		} catch {
			showSnackbar(t("map.fenceCenterNotFound"), "warning");
			return false;
		}
	};

	const handleAddGeofence = () => {
		(async () => {
			const tempName = t("map.fence", { index: geofences.length + 1 });
			const created = await apiCreateGeofence({
				name: tempName,
				enabled: false,
				radiusMeters: 300,
			});
			if (created) {
				const newFence: Geofence = {
					id: String(created.id),
					name: created.name ?? tempName,
					enabled: Boolean(created.enabled),
					center:
						created.centerLatitude && created.centerLongitude
							? {
									latitude: Number(created.centerLatitude),
									longitude: Number(created.centerLongitude),
								}
							: null,
					radiusMeters: Number(created.radiusMeters ?? 300),
				};
				setGeofences((prev) => [...prev, newFence]);
				setSelectedGeofenceId(newFence.id);
				setGeofencePanelOpen(true);
			}
		})();
	};

	const handleDeleteGeofence = (id: string) => {
		(async () => {
			const ok = await apiDeleteGeofence(id);
			if (ok) {
				setGeofences((prev) => prev.filter((g) => g.id !== id));
				if (selectedGeofenceId === id) setSelectedGeofenceId(null);
			}
		})();
	};

	const handleUpdateGeofence = (id: string, patch: Partial<Geofence>) => {
		setGeofences((prev) =>
			prev.map((g) => (g.id === id ? { ...g, ...patch } : g)),
		);
		apiUpdateGeofence(id, patch as Partial<Geofence>).catch(() => {});
	};

	const handlePickCenter = (id: string) => {
		setIsPickingFenceCenter(true);
		setPickingGeofenceId(id);
		setSelectedGeofenceId(id);
		setGeofencePanelOpen(false);
		showSnackbar(t("map.pickingAddress"), "info");
	};

	const handleFocusSelectedGeofence = (id: string) => {
		setSelectedGeofenceId(id);
		setFocusedGeofenceId(null);
		window.setTimeout(() => setFocusedGeofenceId(id), 0);
		if (isMobile) {
			setGeofencePanelOpen(false);
		}
	};

	const toggleGeofencePanel = () => {
		setGeofencePanelOpen((previous) => !previous);
	};

	const toggleVehicleRosterPanel = () => {
		setVehicleRosterOpen((previous) => {
			const next = !previous;
			if (next) setGeofenceHistoryOpen(false);
			return next;
		});
	};

	const toggleGeofenceHistoryPanel = () => {
		setGeofenceHistoryOpen((previous) => {
			const next = !previous;
			if (next) setVehicleRosterOpen(false);
			return next;
		});
	};

	return (
		<LocationProvider>
			<Box
				sx={{
					display: "flex",
					flexDirection: { xs: "column", md: "row" },
					height: "100vh",
					gap: { xs: 1, md: 2 },
					p: { xs: 1, md: 2 },
				}}
			>
				{!isMobile && sidebarOpen && (
					<Box
						sx={{
							width: { md: 440, lg: 500 },
							overflowY: "auto",
							position: "relative",
							height: { md: "92vh" },
							minHeight: { md: 500 },
							maxHeight: { md: 980 },
						}}
					>
						<LocationList
							onSelect={handleSelect}
							{...sharedLocationListProps}
						/>
					</Box>
				)}

				{isMobile && (
					<Drawer
						anchor="left"
						open={sidebarOpen}
						onClose={() => setSidebarOpen(false)}
						PaperProps={{
							sx: { width: "100vw", maxWidth: 460 },
						}}
					>
						<Box sx={{ width: "100%", p: 2 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									mb: 2,
								}}
							>
								<IconButton onClick={() => setSidebarOpen(false)}>
									<CloseIcon fontSize="medium" />
								</IconButton>
							</Box>

							<LocationList
								onSelect={handleSelect}
								{...sharedLocationListProps}
							/>
						</Box>
					</Drawer>
				)}

				<Box
					sx={{
						flex: 1,
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						minHeight: 0,
						gap: { xs: 1, md: 2 },
					}}
				>
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							position: "relative",
							borderRadius: { xs: 0, md: 3 },
							overflow: "hidden",
							boxShadow: { md: 3 },
							height: { xs: "82vh", md: "92vh" },
							minHeight: { xs: 360, md: 500 },
							maxHeight: { xs: 780, md: 980 },
						}}
					>
						{/* <Tooltip
							title={sidebarOpen ? t("map.closeList") : t("map.openList")}
						>
							<IconButton
								onClick={() => setSidebarOpen((prev) => !prev)}
								sx={{
									position: "absolute",
									top: 80,
									left: 5,
									zIndex: 1000,
									backgroundColor: "white",
									boxShadow: 2,
									borderRadius: "50%",
									width: 48,
									height: 48,
									"&:hover": {
										backgroundColor: "white",
										opacity: 1,
									},
								}}
							>
								{sidebarOpen ? <CloseIcon /> : <ListIcon />}
							</IconButton>
						</Tooltip> */}

						<Tooltip
							title={
								vehicleRosterOpen
									? "Fechar vínculos"
									: "Abrir vínculos dos veículos"
							}
						>
							<IconButton
								onClick={toggleVehicleRosterPanel}
								sx={{
									position: "absolute",
									top: 70,
									right: 12,
									zIndex: 1000,
									backgroundColor: "rgba(15, 23, 42, 0.92)",
									color: "white",
									boxShadow: 3,
									borderRadius: "50%",
									width: 48,
									height: 48,
									"&:hover": {
										backgroundColor: "rgba(30, 41, 59, 0.96)",
										opacity: 1,
									},
								}}
							>
								<GroupsIcon />
							</IconButton>
						</Tooltip>

						<Tooltip
							title={
								geofenceHistoryOpen
									? "Fechar histórico"
									: "Abrir histórico de cercas"
							}
						>
							<IconButton
								onClick={toggleGeofenceHistoryPanel}
								sx={{
									position: "absolute",
									top: 126,
									right: 12,
									zIndex: 1000,
									backgroundColor: "rgba(15, 23, 42, 0.92)",
									color: "white",
									boxShadow: 3,
									borderRadius: "50%",
									width: 48,
									height: 48,
									"&:hover": {
										backgroundColor: "rgba(30, 41, 59, 0.96)",
										opacity: 1,
									},
								}}
							>
								<HistoryIcon />
							</IconButton>
						</Tooltip>

						<MapView
							selectedId={selectedId}
							selectedIds={selectedIds}
							onSelect={handleSelect}
							geofences={geofenceConfig}
							geofencePanelOpen={geofencePanelOpen}
							isPickingFenceCenter={isPickingFenceCenter}
							pickingGeofenceId={pickingGeofenceId}
							focusedGeofenceId={focusedGeofenceId}
							onFenceCenterSelected={handleFenceCenterSelected}
							onStopPickingFenceCenter={() => {
								setIsPickingFenceCenter(false);
								setPickingGeofenceId(null);
							}}
							onToggleGeofencePanel={toggleGeofencePanel}
							onStopFollow={handleStopFollow}
						/>

						<Box
							sx={{
								position: "absolute",
								left: { xs: 8, md: 12 },
								right: { xs: 8, md: 12 },
								bottom: { xs: 8, md: 12 },
								zIndex: 900,
								maxHeight: { xs: "44%", md: "41%" },
								overflowY: "auto",
								pointerEvents: "auto",
							}}
						>
							<VehicleCamerasLivePanel selectedId={selectedId} embedded />
						</Box>
					</Box>

					{isMobile ? (
						<Drawer
							anchor="right"
							open={geofencePanelOpen}
							onClose={() => setGeofencePanelOpen(false)}
							ModalProps={{ hideBackdrop: true }}
							PaperProps={{
								sx: {
									width: { xs: "100vw", sm: 420 },
									maxWidth: "100vw",
									background:
										"linear-gradient(180deg, #0f172a 0%, #111827 100%)",
									color: "white",
								},
							}}
						>
							<Box
								sx={{
									p: 2,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 2,
								}}
							>
								<Box>
									<Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
										{t("map.virtualFence")}
									</Typography>
									<Typography variant="body2" sx={{ color: "#cbd5e1" }}>
										{t("map.fenceListDescription")}
									</Typography>
								</Box>
								<IconButton
									onClick={() => setGeofencePanelOpen(false)}
									sx={{ color: "white" }}
								>
									<CloseIcon />
								</IconButton>
							</Box>

							<Box sx={{ p: 2, pt: 0 }}>
								<GeofencePanel
									geofences={geofences}
									selectedId={selectedGeofenceId}
									onSelect={(id: string) => setSelectedGeofenceId(id)}
									onFocusSelected={handleFocusSelectedGeofence}
									onAdd={handleAddGeofence}
									onDelete={handleDeleteGeofence}
									onUpdate={handleUpdateGeofence}
									onPickCenter={handlePickCenter}
									onSearchCenter={handleSearchFenceCenter}
									isPickingCenterForId={
										isPickingFenceCenter ? pickingGeofenceId : null
									}
								/>
							</Box>
						</Drawer>
					) : geofencePanelOpen ? (
						<Box
							sx={{
								flex: "0 0 360px",
								height: { md: "92vh" },
								minHeight: { md: 500 },
								maxHeight: { md: 980 },
								maxWidth: 360,
								minWidth: 360,
							}}
						>
							<Box sx={{ height: "100%" }}>
								<GeofencePanel
									geofences={geofences}
									selectedId={selectedGeofenceId}
									onSelect={(id: string) => setSelectedGeofenceId(id)}
									onFocusSelected={handleFocusSelectedGeofence}
									onAdd={handleAddGeofence}
									onDelete={handleDeleteGeofence}
									onUpdate={handleUpdateGeofence}
									onPickCenter={handlePickCenter}
									onSearchCenter={handleSearchFenceCenter}
									isPickingCenterForId={
										isPickingFenceCenter ? pickingGeofenceId : null
									}
								/>
							</Box>
						</Box>
					) : null}

					<LocationVehicleRosterPanel
						open={vehicleRosterOpen}
						onClose={() => setVehicleRosterOpen(false)}
					/>
					<GeofenceHistoryPanel
						open={geofenceHistoryOpen}
						onClose={() => setGeofenceHistoryOpen(false)}
					/>
				</Box>
			</Box>
		</LocationProvider>
	);
};

export default LocationIndex;
